import { Component, ElementRef, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';
import { environment } from '../../../../../my-app/src/environments/environment';
import { AddRuleDialogComponent } from '../../chart-components/add-rule-dialog/add-rule-dialog.component';

interface ButtonSubmitApiConfig {
  apiUrl?: string;
  url?: string;
  method?: string;
  payload?: Record<string, any>;
  customPayload?: Record<string, any>;
  sourceWidgetId?: string;
  requestDataKey?: string;
  insertRequestKey?: string;
  rowMergeStrategy?: 'insertRequest' | 'mergeFirstRow' | 'mergeAllRows';
  rowFieldMap?: Record<string, string>;
  includeColumns?: string[] | string;
  excludeColumns?: string[] | string;
  skipWidgetRows?: boolean;
  keepMappedSourceColumns?: boolean;
  refreshWidgetId?: string | string[];
  refreshWidgetIds?: string | string[];
  responseWorkflowCtxKey?: string;
  navigateOnSuccess?: boolean | string | number;
  navigateOnError?: boolean | string | number;
  successMessage?: string;
  errorMessage?: string;
  loadingLabel?: string;
  navigateImmediately?: boolean | string | number;
  navigateBeforeSubmit?: boolean | string | number;
}

@Component({
  selector: 'app-button',
  standalone: true,
  imports: [MatButtonModule, CommonModule, MatSnackBarModule],
  templateUrl: './button.component.html',
  styleUrl: './button.component.scss'
})
export class ButtonComponent {
  @Input() data!: any;
  @Input() selectedKey!: string;
  @Output() selectionChange = new EventEmitter<string>();
  @ViewChild('uploadInput') uploadInput?: ElementRef<HTMLInputElement>;

  isUploading = false;
  isSubmitting = false;

  constructor(
    private globalFilterService: GlobalFilterService,
    private http: HttpClient,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) { }

  onClick(value: string) {
    if (this.isUploadDiscountRequestButton()) {
      this.openFilePicker();
      return;
    }

    const submitApiConfig = this.resolveSubmitApiConfig();
    if (submitApiConfig) {
      const navigateImmediately = this.shouldNavigateImmediatelyWithSubmit(submitApiConfig);
      if (navigateImmediately) {
        const navigateTo = String(this.data?.navigateTo || '').trim();
        if (navigateTo) {
          this.router.navigate([navigateTo]);
        }
      }
      this.executeConfiguredSubmitApi(submitApiConfig, navigateImmediately);
      return;
    }

    if (this.data?.navigateTo) {
      this.router.navigate([this.data.navigateTo]);
      return;
    }
    if (this.data?.key === 'nextButton') {
      return;
    }
    if (this.data?.key === 'submitButton') {
      this.submitTableData();
    } else if (this.data?.key === 'cancelButton') {
      this.cancelAction();
    } else if (this.data?.key === 'saveButton') {
      this.saveAction();
    } else if (this.data?.key === 'calculateDiscountButton') {
      this.calculateDiscountAction();
    } else if (this.data?.key === 'computeButton') {
      this.sendQuoteDiscountRequest();
    } else if (this.data?.key === 'resetButton') {
      this.resetAction();
    } else if (this.data?.key === 'addRuleButton') {
      this.openAddRuleDialog();
    } else {
      this.selectionChange.emit(value); // Notify parent

      if (this.data && this.data?.key) {
        this.globalFilterService.updateFilterSelection(
          this.data?.type,
          [value],
          this.data.isQueryParam
        );
      }
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    this.uploadFile(file);
    input.value = '';
  }

  private openFilePicker(): void {
    if (this.isUploading) {
      return;
    }

    this.uploadInput?.nativeElement.click();
  }

  private uploadFile(file: File): void {
    const apiUrl = this.resolveUploadApiUrl();

    if (!apiUrl) {
      if (this.shouldShowUploadErrorMessage()) {
        this.snackBar.open('Upload API URL is not configured for this button.', 'Close', {
          duration: 4000,
        });
      }
      return;
    }

    this.isUploading = true;
    const withCredentials = this.resolveUploadWithCredentials();
    const requestBody = this.buildUploadRequestBody(file);
    const requestOptions: any = {
      withCredentials,
      responseType: this.resolveUploadResponseType()
    };

    if (!(requestBody instanceof FormData)) {
      requestOptions.headers = new HttpHeaders({
        'Content-Type': 'application/octet-stream',
      });
    }

    this.http.post(apiUrl, requestBody, requestOptions).pipe(
      finalize(() => {
        this.isUploading = false;
      })
    ).subscribe({
      next: () => {
        this.refreshWidgetsAfterUploadSuccess();
        if (this.shouldShowUploadSuccessMessage()) {
          this.snackBar.open(`${file.name} uploaded successfully.`, 'Close', {
            duration: 3000,
          });
        }
      },
      error: (err) => {
        console.error('[Button] File upload failed:', err);
        if (this.shouldShowUploadErrorMessage()) {
          const errorMessage = this.resolveErrorMessage(err);
          this.snackBar.open(errorMessage, 'Close', {
            duration: 5000,
          });
        }
      },
    });
  }

  private buildUploadRequestBody(file: File): File | FormData {
    const fieldName = String(this.data?.fileFieldName || this.data?.uploadFieldName || '').trim();
    if (!fieldName) {
      return file;
    }

    const formData = new FormData();
    formData.append(fieldName, file, file.name);
    return formData;
  }

  private isUploadDiscountRequestButton(): boolean {
    return this.data?.widgetId === 'uploaddiscountrequest';
  }

  private resolveUploadApiUrl(): string {
    const baseUrl = this.resolveApiUrl(this.data?.uploadUrl || this.data?.apiUrl || this.data?.url);
    if (!baseUrl) {
      return '';
    }

    const projectCategory = String(this.data?.projectCategory || '').trim();
    const email = String(this.data?.email || '').trim();
    if (!projectCategory && !email) {
      return baseUrl;
    }

    const pathSegments = [baseUrl.replace(/\/+$/, '')];
    if (projectCategory) {
      pathSegments.push(encodeURIComponent(projectCategory));
    }
    if (email) {
      pathSegments.push(encodeURIComponent(email));
    }

    return pathSegments.join('/');
  }

  private resolveUploadWithCredentials(): boolean {
    const configured = this.data?.uploadWithCredentials ?? this.data?.withCredentials;
    if (typeof configured === 'boolean') {
      return configured;
    }
    if (typeof configured === 'number') {
      return configured === 1;
    }
    if (typeof configured === 'string') {
      const normalized = configured.trim().toLowerCase();
      if (['true', '1', 'yes', 'y'].includes(normalized)) {
        return true;
      }
      if (['false', '0', 'no', 'n'].includes(normalized)) {
        return false;
      }
    }

    return false;
  }

  private resolveUploadResponseType(): 'text' | 'json' {
    const configured = String(this.data?.uploadResponseType || '').trim().toLowerCase();
    if (configured === 'json') {
      return 'json';
    }
    return 'text';
  }

  private shouldShowUploadSuccessMessage(): boolean {
    const configured = this.parseBooleanFlag(this.data?.showUploadSuccessMessage);
    if (configured !== null) {
      return configured;
    }
    return true;
  }

  private shouldShowUploadErrorMessage(): boolean {
    const configured = this.parseBooleanFlag(this.data?.showUploadErrorMessage);
    if (configured !== null) {
      return configured;
    }
    return true;
  }

  private refreshWidgetsAfterUploadSuccess(): void {
    const widgetIds = this.resolveRefreshWidgetIds();
    widgetIds.forEach((widgetId) => {
      this.globalFilterService.requestWidgetRefresh(widgetId);
    });
  }

  private resolveRefreshWidgetIds(): string[] {
    const configured = this.data?.refreshWidgetIds ?? this.data?.refreshWidgetId;
    if (Array.isArray(configured)) {
      return configured
        .map((item) => String(item ?? '').trim())
        .filter((item) => item.length > 0);
    }

    const single = String(configured ?? '').trim();
    return single ? [single] : [];
  }

  private parseBooleanFlag(value: any): boolean | null {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      if (value === 1) {
        return true;
      }
      if (value === 0) {
        return false;
      }
      return null;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'y') {
        return true;
      }
      if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === 'n') {
        return false;
      }
    }

    return null;
  }

  private resolveSubmitApiConfig(): ButtonSubmitApiConfig | null {
    const submitApi = this.data?.submitApi;
    if (!submitApi || typeof submitApi !== 'object' || Array.isArray(submitApi)) {
      return null;
    }

    const apiUrl = String(submitApi.apiUrl || submitApi.url || '').trim();
    if (!apiUrl) {
      return null;
    }

    return {
      ...(submitApi as ButtonSubmitApiConfig),
      apiUrl
    };
  }

  private executeConfiguredSubmitApi(config: ButtonSubmitApiConfig, skipNavigateOnResponse = false): void {
    if (this.isSubmitting || this.isUploading) {
      return;
    }

    const apiUrl = this.resolveApiUrl(String(config.apiUrl || config.url || '').trim());
    if (!apiUrl) {
      this.snackBar.open('Submit API URL is not configured for this button.', 'Close', {
        duration: 4000,
      });
      return;
    }

    const method = String(config.method || 'POST').trim().toUpperCase();
    const payload = this.buildConfiguredSubmitPayload(config);
    const requestOptions: Record<string, any> = {};
    if (payload !== undefined && payload !== null) {
      requestOptions['body'] = payload;
    }

    this.isSubmitting = true;
    this.http.request(method, apiUrl, requestOptions).pipe(
      finalize(() => {
        this.isSubmitting = false;
      })
    ).subscribe({
      next: (response) => {
        this.handleConfiguredSubmitSuccess(config, response, skipNavigateOnResponse);
      },
      error: (err) => {
        console.error('[Button] Submit API failed:', err);
        this.handleConfiguredSubmitError(config, err, skipNavigateOnResponse);
      }
    });
  }

  private buildConfiguredSubmitPayload(config: ButtonSubmitApiConfig): Record<string, any> {
    const basePayload = this.toRecord(this.deepClone(config.payload ?? config.customPayload ?? {}));

    if (config.skipWidgetRows === true) {
      return basePayload;
    }

    const requestDataKey = String(config.requestDataKey || 'requestData').trim() || 'requestData';
    const rowMergeStrategy = String(config.rowMergeStrategy || 'insertRequest').trim().toLowerCase();
    const requestData = this.toRecord(basePayload[requestDataKey]);
    const rows = this.buildConfiguredSubmitRows(config);

    if (rowMergeStrategy === 'mergefirstrow') {
      const firstRow = rows[0] || {};
      basePayload[requestDataKey] = { ...requestData, ...firstRow };
      return basePayload;
    }

    if (rowMergeStrategy === 'mergeallrows') {
      const mergedRows = rows.reduce((acc: Record<string, any>, row: Record<string, any>) => {
        return { ...acc, ...row };
      }, {});
      basePayload[requestDataKey] = { ...requestData, ...mergedRows };
      return basePayload;
    }

    const insertRequestKey = String(config.insertRequestKey || 'insert_request').trim() || 'insert_request';
    requestData[insertRequestKey] = rows;
    basePayload[requestDataKey] = requestData;
    return basePayload;
  }

  private buildConfiguredSubmitRows(config: ButtonSubmitApiConfig): Record<string, any>[] {
    const sourceWidgetId = String(config.sourceWidgetId || this.data?.sourceWidgetId || '').trim();
    let sourceRows: any[] = sourceWidgetId
      ? this.globalFilterService.getWidgetData(sourceWidgetId)
      : [];

    if (!Array.isArray(sourceRows) || sourceRows.length === 0) {
      sourceRows = this.globalFilterService.getTableData();
    }

    const includeColumns = this.toStringArray(config.includeColumns);
    const useIncludeColumns = includeColumns.length > 0;
    const excludeKeySet = new Set(
      this.toStringArray(config.excludeColumns).map((key) => this.normalizeKey(key))
    );
    const rowFieldMap = this.toRecord(config.rowFieldMap);
    const keepMappedSourceColumns = config.keepMappedSourceColumns === true;

    return (Array.isArray(sourceRows) ? sourceRows : [])
      .filter((row) => !!row && typeof row === 'object' && !Array.isArray(row))
      .map((row) => {
        const source = row as Record<string, any>;
        const payloadRow: Record<string, any> = {};
        const sourceColumns = useIncludeColumns ? includeColumns : Object.keys(source);

        sourceColumns.forEach((columnKey) => {
          const normalizedKey = String(columnKey || '').trim();
          if (!normalizedKey || excludeKeySet.has(this.normalizeKey(normalizedKey))) {
            return;
          }
          payloadRow[normalizedKey] = source[normalizedKey];
        });

        Object.entries(rowFieldMap).forEach(([targetKey, sourceKey]) => {
          const target = String(targetKey || '').trim();
          const sourcePath = String(sourceKey || '').trim();
          if (!target || !sourcePath || excludeKeySet.has(this.normalizeKey(target))) {
            return;
          }

          payloadRow[target] = this.getValueByPath(source, sourcePath);
          if (!keepMappedSourceColumns && target !== sourcePath) {
            delete payloadRow[sourcePath];
          }
        });

        if (
          (payloadRow['channel'] === undefined || payloadRow['channel'] === null || String(payloadRow['channel']).trim() === '')
          && source['Channel'] !== undefined
        ) {
          payloadRow['channel'] = source['Channel'];
        }

        delete payloadRow['__cellConfig'];
        delete payloadRow['cellConfig'];
        Object.keys(payloadRow).forEach((key) => {
          if (String(key).endsWith('Options')) {
            delete payloadRow[key];
          }
        });

        return payloadRow;
      });
  }

  private handleConfiguredSubmitSuccess(
    config: ButtonSubmitApiConfig,
    response: any,
    skipNavigateOnResponse = false
  ): void {
    const workflowCtxKey = String(config.responseWorkflowCtxKey || '').trim();
    if (workflowCtxKey) {
      this.globalFilterService.setWorkflowCtx({ [workflowCtxKey]: response });
    }

    this.refreshWidgetsByConfig(config);

    const successMessage = String(config.successMessage || '').trim();
    if (!skipNavigateOnResponse && successMessage) {
      this.snackBar.open(successMessage, 'Close', { duration: 3000 });
    }

    if (!skipNavigateOnResponse && this.shouldNavigateAfterSubmit(config, false)) {
      this.router.navigate([this.data.navigateTo]);
    }
  }

  private handleConfiguredSubmitError(
    config: ButtonSubmitApiConfig,
    err: any,
    skipNavigateOnResponse = false
  ): void {
    const configuredErrorMessage = String(config.errorMessage || '').trim();
    const errorMessage = configuredErrorMessage || this.resolveErrorMessage(err) || 'Request failed.';
    this.snackBar.open(errorMessage, 'Close', { duration: 5000 });

    if (!skipNavigateOnResponse && this.shouldNavigateAfterSubmit(config, true)) {
      this.router.navigate([this.data.navigateTo]);
    }
  }

  private shouldNavigateImmediatelyWithSubmit(config: ButtonSubmitApiConfig): boolean {
    const navigateTo = String(this.data?.navigateTo || '').trim();
    if (!navigateTo) {
      return false;
    }

    const rawFlag =
      config.navigateImmediately ??
      config.navigateBeforeSubmit ??
      this.data?.navigateImmediately ??
      this.data?.navigateBeforeSubmit;
    const parsedFlag = this.parseBooleanFlag(rawFlag);
    return parsedFlag === true;
  }

  private shouldNavigateAfterSubmit(config: ButtonSubmitApiConfig, onError: boolean): boolean {
    const navigateTo = String(this.data?.navigateTo || '').trim();
    if (!navigateTo) {
      return false;
    }

    const configuredFlag = onError ? config.navigateOnError : config.navigateOnSuccess;
    const parsedFlag = this.parseBooleanFlag(configuredFlag);
    if (parsedFlag !== null) {
      return parsedFlag;
    }

    return !onError;
  }

  private refreshWidgetsByConfig(config: ButtonSubmitApiConfig): void {
    const configured = config.refreshWidgetIds ?? config.refreshWidgetId ?? this.data?.refreshWidgetIds ?? this.data?.refreshWidgetId;
    const widgetIds = this.toStringArray(configured);
    widgetIds.forEach((widgetId) => {
      this.globalFilterService.requestWidgetRefresh(widgetId);
    });
  }

  private resolveApiUrl(apiUrl?: string): string {
    if (!apiUrl || typeof apiUrl !== 'string') {
      return '';
    }

    return apiUrl.replace('${API_BASE_URL}', environment.API_BASE_URL);
  }

  private resolveErrorMessage(error: any): string {
    if (typeof error?.error === 'string' && error.error.trim()) {
      return error.error;
    }

    if (typeof error?.error?.message === 'string' && error.error.message.trim()) {
      return error.error.message;
    }

    if (typeof error?.message === 'string' && error.message.trim()) {
      return error.message;
    }

    return 'File upload failed.';
  }

  private resetAction(): void {
    this.globalFilterService.resetDashboardState();
  }

  private openAddRuleDialog(): void {
    const dialogRef = this.dialog.open(AddRuleDialogComponent, {
      width: '680px',
      maxWidth: '95vw',
      disableClose: false,
      data: {
        apiUrl: this.data?.apiUrl || '${API_BASE_URL}/spriced/platform',
        entity: this.data?.entity || 'discount_rules',
        batchId: this.data?.batchId || 'test',
        workflowId: this.data?.workflowId || 'ABC-123',
        version: this.data?.version || '1.0'
      }
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        // Find the target widget ID to refresh (e.g. the table widget), 
        // default to 'finalDiscountTable' based on discount rules config
        const refreshWidgetId = this.data?.refreshWidgetId || 'finalDiscountTable';
        this.globalFilterService.requestWidgetRefresh(refreshWidgetId);
      }
    });
  }

  private saveAction() {
    if (this.data?.saveWorkflowContext) {
      this.persistQuoteSummaryInputs(this.data);
    }

    if (!this.data?.apiUrl) {
      return;
    }

    this.http.post(this.data.apiUrl, this.data?.payload || {}).subscribe({
      next: () => { },
      error: (err) => {
        console.error('Error saving data:', err);
      },
    });
  }

  private calculateDiscountAction(): void {
    if (!this.data?.apiUrl) {
      console.error('[Button] calculateDiscountButton apiUrl is missing');
      return;
    }

    this.persistQuoteSummaryInputs(this.data);
    const payload = this.buildQuoteSummaryPayload(this.data);

    this.http.post(this.data.apiUrl, payload).subscribe({
      next: (response: any) => {
        const summaryData = this.extractQuoteSummaryResponseData(response, this.data);
        if (summaryData) {
          this.applyQuoteSummaryResponseMapping(summaryData, this.data);
        }

        this.globalFilterService.setWorkflowCtx({
          quote_summary_request: payload.requestData,
          quote_summary_response: response
        });
      },
      error: (err) => {
        console.error('[Button] Error calculating discount summary:', err);
      }
    });
  }

  private persistQuoteSummaryInputs(config: any): void {
    const quoteSummaryRows = this.collectQuoteSummaryRows(config);
    const discountValues = this.collectQuoteLevelDiscountValues(config);

    this.globalFilterService.setWorkflowCtx({
      quote_summary: quoteSummaryRows,
      quote_level_discounts: discountValues
    });
  }

  private buildQuoteSummaryPayload(config: any): any {
    const workflowCtx = this.globalFilterService.getWorkflowCtx<Record<string, any>>() || {};
    const requestDataStatic = config?.requestDataStatic || {};
    const quoteSummaryRows = this.collectQuoteSummaryRows(config);
    const discountValues = this.collectQuoteLevelDiscountValues(config);

    const requestData = {
      ...requestDataStatic,
      customer: workflowCtx['customer'] ?? requestDataStatic.customer,
      location: workflowCtx['location'] ?? requestDataStatic.location,
      quote_id: workflowCtx['quote_id'] ?? requestDataStatic.quote_id,
      quote_start_date: workflowCtx['quote_start_date'] ?? requestDataStatic.quote_start_date,
      quote_end_date: workflowCtx['quote_end_date'] ?? workflowCtx['deal_end_date'] ?? requestDataStatic.quote_end_date,
      quote_name: workflowCtx['quote_name'] ?? requestDataStatic.quote_name,
      running_qty: workflowCtx['running_qty'] ?? requestDataStatic.running_qty,
      commitment: workflowCtx['commitment'] ?? requestDataStatic.commitment,
      discount: discountValues,
      quote_summary: quoteSummaryRows
    };

    return {
      requestData,
      responseList: config?.responseList ?? {},
      entity: config?.entity,
      workflowId: config?.workflowId,
      endpoints: config?.endpoints || [],
      communicationType: config?.communicationType || 'rest',
      version: config?.version || '1.0'
    };
  }

  private collectQuoteSummaryRows(config: any): any[] {
    const widgetId = config?.quotePartsWidgetId || 'quotePartsTable';
    const rows = this.globalFilterService.getWidgetData(widgetId);
    const sourceRows = rows.length > 0 ? rows : this.globalFilterService.getTableData();

    const itemNameKey = config?.quoteSummaryItemNameKey || 'item_name';
    const bundleKey = config?.quoteSummaryBundleKey || 'bundle';
    const discountedPriceKey = config?.quoteSummaryDiscountedPriceKey || 'discounted_price';
    const unitPriceKey = config?.quoteSummaryUnitPriceKey || 'unit_price';

    return sourceRows
      .map((row: any) => ({
        item_name: row?.[itemNameKey] ?? '',
        bundle: row?.[bundleKey] ?? '',
        discounted_price: row?.[discountedPriceKey] ?? '',
        unit_price: row?.[unitPriceKey] ?? ''
      }))
      .filter((row: any) => row.item_name || row.bundle || row.discounted_price || row.unit_price);
  }

  private collectQuoteLevelDiscountValues(config: any): string[] {
    const widgetId = config?.quoteLevelDiscountWidgetId || 'quoteLevelDiscountTable';
    const rows = this.globalFilterService.getWidgetData(widgetId);
    const discountFieldKey = config?.quoteLevelDiscountField || 'quote_level_discount';

    return rows
      .flatMap((row: any) => {
        const value = row?.[discountFieldKey];
        return Array.isArray(value) ? value : [value];
      })
      .map((value: any) => String(value ?? '').trim())
      .filter((value: string) => value.length > 0);
  }

  private extractQuoteSummaryResponseData(response: any, config: any): any {
    const entityKey = config?.responseEntityKey || config?.entity || 'quote_summary';
    return response?.responseList?.[1]?.[entityKey]?.[0]?.data || null;
  }

  private applyQuoteSummaryResponseMapping(summaryData: any, config: any): void {
    const finalDiscountWidgetId = config?.finalDiscountWidgetId || 'finalDiscountTable';
    const dealImpactWidgetId = config?.dealImpactWidgetId || 'dealImpactTable';
    const workflowCtx = this.globalFilterService.getWorkflowCtx<Record<string, any>>() || {};
    const fallbackCommitment = config?.requestDataStatic?.commitment ?? '';
    const discountPercentage =
      summaryData?.discount_percentage ??
      summaryData?.total_discount_percentage ??
      summaryData?.total_discount_percent ??
      summaryData?.total_discount_pct;

    const finalDiscountRows = [
      {
        running_qty: workflowCtx?.['running_qty'] ?? '',
        commitment: workflowCtx?.['commitment'] ?? fallbackCommitment ?? '',
        sales_before_discount: summaryData?.sales_before_discount ?? '',
        total_discount: this.formatTotalDiscountDisplay(summaryData?.total_discount, discountPercentage),
        sales_after_discount: summaryData?.sales_after_discount ?? ''
      }
    ];

    const dealImpactRows = [
      {
        previous_sales: summaryData?.previous_sales ?? '',
        deal_impact: summaryData?.deal_impact ?? ''
      }
    ];

    this.globalFilterService.setWidgetData(finalDiscountWidgetId, finalDiscountRows);
    this.globalFilterService.setWidgetData(dealImpactWidgetId, dealImpactRows);
    this.globalFilterService.setWorkflowCtx({ quote_summary_result: summaryData });
  }

  private formatTotalDiscountDisplay(amountValue: any, percentageValue: any): string {
    const formattedAmount = this.formatAmountWithCommas(amountValue);
    const formattedPercentage = this.formatPercentageValue(percentageValue);

    if (!formattedAmount && !formattedPercentage) {
      return '';
    }
    if (!formattedPercentage) {
      return formattedAmount;
    }
    if (!formattedAmount) {
      return formattedPercentage;
    }

    return `${formattedAmount} (${formattedPercentage})`;
  }

  private formatAmountWithCommas(value: any): string {
    if (value === undefined || value === null || value === '') {
      return '';
    }

    if (typeof value === 'number') {
      return this.formatNumericAmount(value);
    }

    const raw = String(value).trim();
    if (!raw) {
      return '';
    }

    const sanitized = raw.replace(/,/g, '');
    const parsed = Number(sanitized);
    if (Number.isNaN(parsed)) {
      return raw;
    }

    return this.formatNumericAmount(parsed);
  }

  private formatNumericAmount(value: number): string {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }

  private formatPercentageValue(value: any): string {
    if (value === undefined || value === null || value === '') {
      return '';
    }

    if (typeof value === 'number') {
      const normalized = Math.abs(value) <= 1 ? value * 100 : value;
      return `${this.formatNumericAmount(normalized)}%`;
    }

    const raw = String(value).trim();
    if (!raw) {
      return '';
    }

    if (raw.endsWith('%')) {
      return raw;
    }

    const sanitized = raw.replace(/,/g, '');
    const parsed = Number(sanitized);
    if (Number.isNaN(parsed)) {
      return raw;
    }

    const normalized = Math.abs(parsed) <= 1 ? parsed * 100 : parsed;
    return `${this.formatNumericAmount(normalized)}%`;
  }

  private cancelAction() {
    // Clear the table data
    this.globalFilterService.setTableData([]);
    // Reset filters
    this.globalFilterService.clearAllFilters();
  }

  private submitTableData() {
    const tableData = this.globalFilterService.getTableData();

    console.log(' 2345 tableData', tableData);

    // Calculate total cost from BOM data
    let totalCost = 0;
    tableData.forEach((item: any) => {
      if (this.data.responseKey === "Total cost (2025)") {
        totalCost += parseFloat(item.cost_2025 || 0);
      } else if (this.data.responseKey === "Total Price (2025)") {
        totalCost += parseFloat(item.price_2025 || 0);
      }
    });

    const payload = {
      requestData: {
        source: "UI",
        quote_bom: tableData.map((item: any) => ({
          quote_id: item.quote_id || "QT-10001",
          ...item
        })),
      },
      responseList: [],
      entity: "quote_bom",
      workflowId: "BOM_WORKFLOW_001",
      endpoints: [
        {
          functionName: "BomInsertUpdateService",
          version: "1.0"
        }
      ],
      communicationType: "rest",
      version: "1.0"
    };

    // Update stat with calculated total from request data
    if (totalCost > 0) {
      console.log('[Button] Updating totalCost stat with calculated value:', totalCost);
      this.globalFilterService.updateStat('totalCost', totalCost);
    }

    this.http.post(this.data.apiUrl, payload).subscribe({
      next: (response: any) => {
        console.log('[Button] Submit response received:', response);
      },
      error: (err) => {
        console.error('Error submitting data:', err);
      }
    });
  }

  private sendQuoteDiscountRequest(): void {
    if (!this.data?.apiUrl) {
      console.error('[Button] computeButton apiUrl is missing');
      return;
    }

    const sourceWidgetId = this.data?.sourceWidgetId || this.data?.quotePartsWidgetId || 'finalDiscountTable';
    let items = this.globalFilterService.getWidgetData(sourceWidgetId);
    if (!items || items.length === 0) {
      items = this.globalFilterService.getTableData();
    }

    const requestItems = (items || []).map((item: any) => ({
      quote_id: item.quote_id,
      quote_name: item.quote_name,
      unit_price: item.unit_price,
      discount_percentage: item.discount_percentage ?? item.line_discount ?? 0
    }));

    const payload = {
      requestData: {
        items: requestItems
      },
      responseList: {},
      entity: this.data?.entity || 'quote_discount_request',
      workflowId: this.data?.workflowId || 'ITEM_MASTER_001',
      endpoints: this.data?.endpoints || [
        {
          functionName: 'QuoteDiscountCalculatorService',
          version: '1.0'
        }
      ],
      communicationType: this.data?.communicationType || 'rest',
      version: this.data?.version || '1.0'
    };

    console.log('[Button] Sending quote discount request to', this.data.apiUrl, payload);

    this.http.post(this.data.apiUrl, payload).subscribe({
      next: (response: any) => {
        console.log('[Button] compute response received:', response);
      },
      error: (err) => {
        console.error('[Button] Error sending quote discount request:', err);
      }
    });
  }

  getButtonClasses(): string {
    let classes = 'btn';
    if (this.data?.key === 'cancelButton') {
      classes += ' btn-cancel';
    }
    if (this.isQuoteDetailsRoute()) {
      classes += ' btn-quote-details';
    }
    if (this.isQuoteDetailsRoute() && this.data?.key === 'calculateDiscountButton') {
      classes += ' btn-calculate-impact';
    }
    if (this.isSelected()) {
      classes += ' btn-selected';
    }
    if (this.data?.class) {
      classes += ` ${this.data.class}`;
    }
    return classes;
  }

  isSelected(): boolean {
    return this.selectedKey === this.data.key;
  }

  getButtonLabel(): string {
    if (this.isUploadDiscountRequestButton() && this.isUploading) {
      return 'Uploading...';
    }

    if (this.isSubmitting) {
      const submitApiConfig = this.resolveSubmitApiConfig();
      const loadingLabel = String(submitApiConfig?.loadingLabel || '').trim();
      return loadingLabel || 'Processing...';
    }

    return this.data?.label || '';
  }

  private isQuoteDetailsRoute(): boolean {
    const currentPath = (this.router.url || '').split('?')[0];
    return currentPath === '/quote-parts-2';
  }

  private toRecord(value: any): Record<string, any> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {};
    }
    return { ...(value as Record<string, any>) };
  }

  private toStringArray(value: any): string[] {
    if (Array.isArray(value)) {
      return value
        .map((item) => String(item ?? '').trim())
        .filter((item) => item.length > 0);
    }

    if (typeof value === 'string') {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    }

    return [];
  }

  private normalizeKey(value: string): string {
    return String(value || '').trim().toLowerCase();
  }

  private getValueByPath(source: any, path: string): any {
    return String(path || '')
      .split('.')
      .map((segment) => segment.trim())
      .filter((segment) => segment.length > 0)
      .reduce((acc, segment) => {
        if (acc === undefined || acc === null) {
          return undefined;
        }

        const arrayMatch = segment.match(/^([^[\]]+)\[(\d+)\]$/);
        if (!arrayMatch) {
          return acc?.[segment];
        }

        const value = acc?.[arrayMatch[1]];
        return Array.isArray(value) ? value[Number(arrayMatch[2])] : undefined;
      }, source);
  }

  private deepClone<T>(value: T): T {
    if (value === undefined || value === null) {
      return value;
    }
    return JSON.parse(JSON.stringify(value));
  }
}
